import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import './App.css';

import Layout from './components/Layout';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import CallbackPage from './pages/CallbackPage';
import SuccessPage from './pages/SuccessPage';
import DashboardPage from './pages/DashboardPage';
import ExposuresPage from './pages/ExposuresPage';
import RecommendationsPage from './pages/RecommendationsPage';
import PrivacyAssistantPage from './pages/PrivacyAssistantPage';
import AdInsightsPage from './pages/AdInsightsPage';
import AdPreferencesPage from './pages/AdPreferencesPage';
import AdvertisersPage from './pages/AdvertisersPage';
import CategoriesPage from './pages/CategoriesPage';

// Services
import { authService } from './services/authService';
import { resolveActivePlatform, platformSupportsAdInsights } from './utils/resolveActivePlatform';
import { tokens } from './theme/tokens';

const fontStack = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: tokens.accent.brandTeal,
      light: '#40D2B7',
      dark: '#109078',
      contrastText: '#ffffff',
    },
    secondary: {
      main: tokens.accent.brandBlue,
      light: '#60A5FA',
      dark: '#1D4ED8',
      contrastText: '#ffffff',
    },
    success: {
      main: tokens.accent.safe,
      light: '#4ADE80',
      dark: '#15803D',
    },
    warning: {
      main: tokens.accent.warning,
      light: '#FBBF24',
      dark: '#B45309',
    },
    error: {
      main: tokens.accent.danger,
      light: '#F87171',
      dark: '#B91C1C',
    },
    background: {
      default: tokens.bg.app,
      paper: tokens.bg.card,
    },
    text: {
      primary: tokens.text.primary,
      secondary: tokens.text.secondary,
    },
    divider: tokens.border.strong,
  },
  shape: {
    borderRadius: 14,
  },
  typography: {
    fontFamily: fontStack,
    h1: { fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontWeight: 600, letterSpacing: '-0.015em' },
    h4: { fontWeight: 600, letterSpacing: '-0.015em', lineHeight: 1.25 },
    h5: { fontWeight: 600, lineHeight: 1.3 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.55 },
    subtitle2: { fontWeight: 600, fontSize: '1rem', lineHeight: 1.5 },
    body1: { fontSize: '1.0625rem', lineHeight: 1.65 },
    body2: { fontSize: '0.9375rem', lineHeight: 1.6 },
    caption: { fontSize: '0.8125rem', lineHeight: 1.5 },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.02em' },
  },
  shadows: [
    'none',
    '0 1px 2px rgba(42, 49, 66, 0.06)',
    '0 2px 8px rgba(42, 49, 66, 0.08)',
    '0 4px 16px rgba(42, 49, 66, 0.09)',
    '0 6px 20px rgba(42, 49, 66, 0.1)',
    '0 8px 24px rgba(42, 49, 66, 0.11)',
    '0 10px 28px rgba(42, 49, 66, 0.12)',
    '0 12px 32px rgba(42, 49, 66, 0.13)',
    '0 14px 36px rgba(42, 49, 66, 0.14)',
    '0 16px 40px rgba(42, 49, 66, 0.15)',
    '0 18px 44px rgba(42, 49, 66, 0.16)',
    '0 20px 48px rgba(42, 49, 66, 0.17)',
    '0 22px 52px rgba(42, 49, 66, 0.18)',
    '0 24px 56px rgba(42, 49, 66, 0.19)',
    '0 26px 60px rgba(42, 49, 66, 0.2)',
    '0 28px 64px rgba(42, 49, 66, 0.21)',
    '0 30px 68px rgba(42, 49, 66, 0.22)',
    '0 32px 72px rgba(42, 49, 66, 0.23)',
    '0 34px 76px rgba(42, 49, 66, 0.24)',
    '0 36px 80px rgba(42, 49, 66, 0.25)',
    '0 38px 84px rgba(42, 49, 66, 0.26)',
    '0 40px 88px rgba(42, 49, 66, 0.27)',
    '0 42px 92px rgba(42, 49, 66, 0.28)',
    '0 44px 96px rgba(42, 49, 66, 0.29)',
    '0 46px 100px rgba(42, 49, 66, 0.3)',
  ],
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: tokens.bg.app,
          backgroundAttachment: 'fixed',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.control,
          padding: '8px 18px',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
          '&:focus-visible': {
            boxShadow: `0 0 0 2px ${tokens.bg.app}, 0 0 0 4px ${alpha(tokens.accent.cyan, 0.85)}`,
          },
        },
        sizeLarge: {
          minHeight: 44,
          padding: '10px 22px',
          fontSize: '0.9375rem',
        },
        contained: {
          '&:hover': {
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        containedPrimary: {
          boxShadow: `0 2px 14px ${alpha(tokens.accent.brandTeal, 0.35)}`,
          '&:hover': {
            boxShadow: `0 4px 20px ${alpha(tokens.accent.brandTeal, 0.45)}`,
          },
          '&:active': {
            boxShadow: `0 1px 8px ${alpha(tokens.accent.brandTeal, 0.3)}`,
          },
        },
        containedSecondary: {
          boxShadow: `0 2px 14px ${alpha(tokens.accent.brandBlue, 0.32)}`,
          '&:hover': {
            boxShadow: `0 4px 20px ${alpha(tokens.accent.brandBlue, 0.42)}`,
          },
          '&:active': {
            boxShadow: `0 1px 8px ${alpha(tokens.accent.brandBlue, 0.28)}`,
          },
        },
        outlined: {
          borderWidth: 1.5,
          '&:hover': {
            borderWidth: 1.5,
            transform: 'translateY(-1px)',
            boxShadow: `0 2px 12px ${alpha(tokens.accent.cyan, 0.12)}`,
          },
        },
        text: {
          '&:hover': {
            boxShadow: `0 0 0 1px ${alpha(tokens.accent.cyan, 0.15)}`,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.card,
          boxShadow: '0 10px 32px rgba(2, 6, 23, 0.5)',
          backgroundColor: tokens.bg.card,
          backgroundImage: 'none',
          border: `1px solid ${tokens.border.subtle}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.bg.card,
          backgroundImage: 'none',
          border: `1px solid ${tokens.border.subtle}`,
        },
        elevation1: {
          boxShadow: '0 2px 10px rgba(42, 49, 66, 0.06)',
        },
        elevation2: {
          boxShadow: '0 4px 18px rgba(42, 49, 66, 0.08)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = authService.isAuthenticated();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Wrap a page with the sidebar layout for authenticated users
const AuthLayout = ({ children }) => (
  <ProtectedRoute>
    <Layout>{children}</Layout>
  </ProtectedRoute>
);

/** Ad insights pages are only for YouTube / Facebook (DYI); Reddit users are redirected. */
const AdDataRoute = ({ children }) => {
  const user = authService.getStoredUser();
  const platform = resolveActivePlatform(user);
  if (!platformSupportsAdInsights(platform)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          {/* Public routes — no sidebar */}
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/callback" element={<CallbackPage />} />
          <Route path="/success" element={<SuccessPage />} />

          {/* Authenticated routes — sidebar layout */}
          <Route path="/dashboard" element={<AuthLayout><DashboardPage /></AuthLayout>} />
          <Route path="/exposures" element={<AuthLayout><ExposuresPage /></AuthLayout>} />
          <Route path="/recommendations" element={<AuthLayout><RecommendationsPage /></AuthLayout>} />
          <Route path="/privacy-assistant" element={<AuthLayout><PrivacyAssistantPage /></AuthLayout>} />
          <Route
            path="/ad-insights"
            element={
              <AuthLayout>
                <AdDataRoute>
                  <AdInsightsPage />
                </AdDataRoute>
              </AuthLayout>
            }
          />
          <Route
            path="/ad-preferences"
            element={
              <AuthLayout>
                <AdDataRoute>
                  <AdPreferencesPage />
                </AdDataRoute>
              </AuthLayout>
            }
          />
          <Route
            path="/advertisers"
            element={
              <AuthLayout>
                <AdDataRoute>
                  <AdvertisersPage />
                </AdDataRoute>
              </AuthLayout>
            }
          />
          <Route
            path="/categories"
            element={
              <AuthLayout>
                <AdDataRoute>
                  <CategoriesPage />
                </AdDataRoute>
              </AuthLayout>
            }
          />

          {/* Redirects */}
          <Route path="/facebook" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
